import { documentToReactComponents, Options } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { Document, Node } from "@contentful/rich-text-types"

export const collectEmbeddedEntryIds = (node: Node | Document | (Node | Document)[] | undefined): string[] => {
  if (!node) return []
  const nodesToTraverse = Array.isArray(node) ? node : [node]
  const embeddedEntryIds: string[] = []
  const traverseNode = (nodeToVisit: Node | Document | undefined) => {
    if (!nodeToVisit) return
    const currentNode = nodeToVisit as { nodeType?: string; data?: { target?: { sys?: { id?: string } } }; content?: (Node | Document)[] }
    const currentNodeType = currentNode.nodeType
    const embeddedEntryId = getTargetEntryId(nodeToVisit as Node)
    if ((currentNodeType === 'embedded-entry-block' || currentNodeType === 'embedded-entry-inline' || currentNodeType === 'entry-hyperlink') && embeddedEntryId) {
      embeddedEntryIds.push(embeddedEntryId)
    }
    if (currentNode.content && Array.isArray(currentNode.content)) {
      currentNode.content.forEach(traverseNode)
    }
  }
  nodesToTraverse.forEach(traverseNode)
  return embeddedEntryIds
}

const getTargetEntryId = (node: Node | Document): string | undefined => {
  const nodeData = (node as { data?: { target?: { sys?: { id?: string } } } }).data
  const embeddedEntryId = nodeData?.target?.sys?.id
  return typeof embeddedEntryId === 'string' ? embeddedEntryId : undefined
}
const renderHyperlink = (node: Node, children: React.ReactNode) => {
  const href = (node as { data?: { uri?: string } })?.data?.uri
  if (!href || typeof href !== 'string') return children
  return (
    <a
      href={href}
      target="_blank"
      className="underline text-blue-600 dark:text-blue-400"
    >
      {children}
    </a>
  )
}

const listItemInnerOptions: Options = {
  renderNode: {
    [INLINES.HYPERLINK]: renderHyperlink,
    [BLOCKS.PARAGRAPH]: (_node, children: React.ReactNode) => <>{children}</>, // li 안에서는 p 제거
  },
}

const renderListItemChildren = (node: Node) => {
  const listItemDocument: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: (node as Document).content || [],
  }
  return documentToReactComponents(listItemDocument, listItemInnerOptions)
}

type TermsEntryMinimal = { fields: { title?: string; name?: string; body?: Document } }
type TermsIncludes = Record<string, TermsEntryMinimal>

export const EntryToggle = ({ entryId, includes }: { entryId: string, includes: TermsIncludes }) => {
  const embeddedEntryMap = includes ?? {}
  const embeddedEntry = embeddedEntryMap[entryId]
  if (!embeddedEntry) return null
  const embeddedFields = embeddedEntry.fields ?? {}
  const titleField = typeof embeddedFields.title === 'string' ? embeddedFields.title : ''
  const nameField = typeof embeddedFields.name === 'string' ? embeddedFields.name : ''
  const displayTitle = titleField || nameField
  const bodyDocument = embeddedFields.body

  return (
    <details className="p-3" style={{ marginTop: '10px' }}>
      <summary className="cursor-pointer select-none font-pretendard-semibold text-[14px] text-black dark:text-white">
        {displayTitle}
      </summary>
      <div className="mt-2">
        {bodyDocument && documentToReactComponents(bodyDocument, createTermsRenderOptions(embeddedEntryMap))}
      </div>
    </details>
  )
}

export const createTermsRenderOptions = (entriesById: TermsIncludes): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node, children: React.ReactNode) => (
      <p className="font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2 leading-[28px]"
        style={{ marginTop: '10px' }}>
        {children}
      </p>
    ),
    [INLINES.HYPERLINK]: renderHyperlink,
    [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
      const embeddedEntryId = getTargetEntryId(node)
      if (!embeddedEntryId) return null
      return <EntryToggle entryId={embeddedEntryId} includes={entriesById} />
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
      const embeddedEntryId = getTargetEntryId(node)
      if (!embeddedEntryId) return null
      return <EntryToggle entryId={embeddedEntryId} includes={entriesById} />
    },
    [INLINES.ENTRY_HYPERLINK]: (node: Node, children: React.ReactNode) => {
      const embeddedEntryId = getTargetEntryId(node)
      if (!embeddedEntryId) return <>{children}</>
      return <EntryToggle entryId={embeddedEntryId} includes={entriesById} />
    },
    [BLOCKS.HEADING_1]: (_node, children: React.ReactNode) => (
      <h1 className="font-pretendard-semibold text-[32px] text-main-light-2 dark:text-main-dark-2 leading-[28px]">
        {children}
      </h1>
    ),
    [BLOCKS.HEADING_3]: (_node, children: React.ReactNode) => (
      <h3 className="font-pretendard-semibold text-[20px] text-main-light-2 dark:text-main-dark-2 leading-[28px] my-[4px]"
        style={{ marginTop: '10px' }}>
        {children}
      </h3>
    ),
    [BLOCKS.UL_LIST]: (_node, children: React.ReactNode) => (
      <ul className="pl-5 space-y-1 font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2 leading-[28px]"
        style={{
          listStyleType: 'disc',
          listStylePosition: 'inside',
        }}>
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (_node, children: React.ReactNode) => (
      <ol className="pl-5 space-y-1 font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2 leading-[28px]"
        style={{
          listStyleType: 'decimal',
          listStylePosition: 'inside',
        }}>
        {children}
      </ol>
    ),
    [BLOCKS.LIST_ITEM]: (node: Node) => (
      <li className="marker:text-main-light-2 dark:marker:text-main-dark-2 leading-[28px] pl-3"
        style={{
          marginTop: '10px',
          whiteSpace: 'pre-line',

        }}>
        {renderListItemChildren(node)}
      </li>
    ),

  },
})